import FormularioButom from "./FormularioButom";
import FormularioInput from "./FormularioInput";
import FormularioList from "./FormularioList";
import { FiChevronDown, FiChevronUp, FiPlus, FiTrash2 } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";
import "./FormularioComissao.css";
import Api from "../Services/Api";

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const n = toFiniteNumber(value);
    if (n !== null) return n;
  }
  return null;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function resolvePercent(itemPercent, generalPercent) {
  const item = toFiniteNumber(itemPercent);
  if (item !== null) return item;
  return generalPercent;
}

function calcCommissionValue(itemTotal, percent) {
  const total = toNumber(itemTotal, 0);
  const pct = toNumber(percent, 0);
  return Number((total * (pct / 100)).toFixed(2));
}

function formatCurrencyBRL(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "R$ 0,00";
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
}

function parseJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function readPath(obj, path) {
  if (!obj || typeof obj !== "object" || !path) return null;
  const value = String(path)
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
  return value === undefined ? null : value;
}

function asItemsArray(rawInput) {
  if (!rawInput) return [];

  const parsed = parseJson(rawInput);
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed !== "object") return [];

  const candidates = [
    parsed.commission_items,
    parsed.commissionItems,
    parsed.items,
    parsed.itens,
    parsed.products,
    parsed.produtos,
    parsed.lista_itens,
    parsed.data?.commission_items,
    parsed.data?.items,
    parsed.payload?.commission_items,
    parsed.payload?.items,
  ];

  for (const candidate of candidates) {
    const candidateParsed = parseJson(candidate);
    if (Array.isArray(candidateParsed)) return candidateParsed;
  }

  return [];
}

function parseSavedCommissionItems(rawInput) {
  const baseItems = asItemsArray(rawInput);
  if (!baseItems.length) return [];

  return baseItems
    .map((item, index) => ({
      row_key: `${String(item?.item_code || "ITEM")}_${index}`,
      item_code: String(
        pickFirst(item?.item_code, item?.codigo_item, item?.codigo_produto, `ITEM_${index + 1}`)
      ),
      description: String(
        pickFirst(item?.description, item?.descricao, item?.nome, `Item ${index + 1}`)
      ),
      quantity: Math.max(1, toNumber(pickFirst(item?.quantity, item?.quantidade), 1)),
      unit_price: toNumber(
        pickFirst(item?.unit_price, item?.valor_unitario, item?.preco_unitario),
        0
      ),
      item_total: toNumber(
        pickFirst(item?.item_total, item?.valor_total, item?.total, item?.subtotal),
        0
      ),
      commission_percent:
        pickFirst(
          item?.commission_percent,
          item?.percentual_comissao,
          item?.percentual,
          item?.comissao_percentual
        ) === null
          ? ""
          : String(
              pickFirst(
                item?.commission_percent,
                item?.percentual_comissao,
                item?.percentual,
                item?.comissao_percentual
              )
            ),
    }))
    .filter(Boolean);
}

function extractRawOrder(rawInput) {
  const parsed = parseJson(rawInput);
  if (!parsed) return null;

  if (parsed.pedido_venda_produto) return parsed.pedido_venda_produto;
  if (parsed.PedidoVendaProduto) return parsed.PedidoVendaProduto;
  return parsed;
}

function extractOrderItems(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") return [];

  const candidates = [
    raw.det,
    raw.DET,
    raw.itens,
    raw.Itens,
    raw.items,
    raw.produtos,
    raw.lista_itens,
    raw.pedido_itens,
  ];

  const baseItems = candidates.find((candidate) => Array.isArray(candidate)) || [];

  return baseItems
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;

      const produto = item.produto || item.Produto || {};
      const ide = item.ide || item.Ide || {};

      const quantity = Math.max(
        1,
        toNumber(pickFirst(produto.quantidade, item.quantidade, produto.qtde, item.qtde, 1), 1)
      );
      const unit_price = toNumber(
        pickFirst(
          produto.valor_unitario,
          item.valor_unitario,
          produto.preco_unitario,
          item.preco_unitario
        ),
        0
      );

      const gross_total = pickFirstNumber(
        produto.valor_mercadoria,
        item.valor_mercadoria,
        produto.valor_total_bruto,
        item.valor_total_bruto
      );
      const item_discount = pickFirstNumber(
        produto.valor_desconto,
        item.valor_desconto,
        produto.desconto,
        item.desconto
      );

      let item_total = pickFirstNumber(
        produto.valor_total,
        item.valor_total,
        produto.subtotal,
        item.subtotal
      );
      if (item_total === null && gross_total !== null && item_discount !== null) {
        item_total = Number((gross_total - item_discount).toFixed(2));
      }
      if (item_total === null) {
        item_total = Number((quantity * unit_price).toFixed(2));
      }

      const description = String(
        pickFirst(
          produto.descricao,
          produto.descricao_produto,
          item.descricao,
          item.nome,
          `Item ${index + 1}`
        )
      ).trim();

      const item_code = String(
        pickFirst(
          produto.codigo_produto,
          item.codigo_produto,
          ide.codigo_item,
          item.codigo_item,
          item.id,
          `ITEM_${index + 1}`
        )
      ).trim();

      return {
        row_key: `${item_code}_${index}`,
        item_code,
        description,
        quantity,
        unit_price,
        item_total: toNumber(item_total, 0),
        commission_percent: "",
      };
    })
    .filter(Boolean);
}

function extractOrderValues(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") {
    return {
      total_value: null,
      freight_value: null,
      products_value: null,
      discount_value: null,
      products_net_value: null,
    };
  }

  const totals = raw.total_pedido || raw.TotalPedido || raw.totais || {};
  const freightBlock = raw.frete || raw.Frete || {};
  const discountBlock = raw.desconto || raw.Desconto || {};

  const total_value = pickFirstNumber(
    totals.valor_total_pedido,
    raw.valor_total_pedido,
    raw.total,
    totals.valor_total
  );

  let freight_value = pickFirstNumber(
    totals.valor_frete,
    totals.frete,
    totals.total_frete,
    raw.valor_frete,
    raw.total_frete,
    freightBlock.valor_frete,
    freightBlock.frete,
    freightBlock.total_frete,
    readPath(raw, "transporte.valor_frete"),
    readPath(raw, "Transporte.valor_frete")
  );

  const products_value = pickFirstNumber(
    totals.valor_mercadorias,
    totals.total_mercadorias,
    totals.valor_total_mercadorias,
    totals.total_produtos,
    totals.valor_total_produtos,
    totals.subtotal,
    raw.valor_mercadorias,
    raw.total_mercadorias,
    raw.valor_total_mercadorias,
    raw.total_produtos,
    raw.valor_total_produtos,
    raw.subtotal
  );

  let discount_value = pickFirstNumber(
    totals.valor_desconto,
    totals.valor_descontos,
    totals.desconto,
    totals.total_desconto,
    totals.desc_total,
    totals.valor_desc,
    raw.valor_desconto,
    raw.valor_descontos,
    raw.total_desconto,
    raw.desc_total,
    raw.valor_desc,
    discountBlock.valor_desconto,
    discountBlock.valor_descontos,
    discountBlock.desconto,
    discountBlock.valor,
    readPath(raw, "total_pedido.valor_desconto"),
    readPath(raw, "total_pedido.valor_descontos"),
    readPath(raw, "TotalPedido.valor_desconto"),
    readPath(raw, "TotalPedido.valor_descontos")
  );

  if (discount_value === null && total_value !== null && products_value !== null && freight_value !== null) {
    const inferredDiscount = Number((products_value + freight_value - total_value).toFixed(2));
    if (Number.isFinite(inferredDiscount) && inferredDiscount > 0) {
      discount_value = inferredDiscount;
    }
  }

  let products_net_value = null;
  if (products_value !== null && discount_value !== null) {
    products_net_value = Number((products_value - discount_value).toFixed(2));
  } else if (total_value !== null && freight_value !== null) {
    products_net_value = Number((total_value - freight_value).toFixed(2));
  } else if (products_value !== null) {
    products_net_value = Number(products_value.toFixed(2));
  }

  if (freight_value === null && total_value !== null && products_net_value !== null) {
    freight_value = Number((total_value - products_net_value).toFixed(2));
  }

  if (products_net_value !== null && products_net_value < 0) products_net_value = 0;
  if (freight_value !== null && freight_value < 0) freight_value = 0;
  if (discount_value !== null && discount_value < 0) discount_value = 0;

  return { total_value, freight_value, products_value, discount_value, products_net_value };
}

function extractOrderInstallments(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") return [];

  const cabecalho = raw.cabecalho || raw.Cabecalho || {};
  const blocoParcelas = raw.lista_parcelas || raw.ListaParcelas || raw.parcelas || raw.Parcelas || {};
  const parcelasRaw = pickFirst(
    blocoParcelas.parcela,
    blocoParcelas.Parcela,
    raw.parcela,
    raw.parcelas
  );

  let parcelas = (Array.isArray(parcelasRaw) ? parcelasRaw : parcelasRaw ? [parcelasRaw] : [])
    .filter((item) => item && typeof item === "object")
    .map((item, index) => {
      const numeroParcela = Number(
        pickFirst(item.numero_parcela, item.parcela, item.numero, index + 1)
      );

      return {
        numero_parcela: Number.isFinite(numeroParcela) && numeroParcela > 0 ? numeroParcela : index + 1,
        valor: toFiniteNumber(pickFirst(item.valor, item.valor_parcela, item.valor_documento)),
        percentual: toFiniteNumber(pickFirst(item.percentual, item.percentual_parcela)),
        tipo_documento: String(item.tipo_documento || "").trim().toUpperCase(),
        meio_pagamento: String(item.meio_pagamento || "").trim().toUpperCase(),
      };
    });

  const qtdCabecalho = Number(toFiniteNumber(cabecalho?.qtde_parcelas) || 0);
  if (parcelas.length === 0 && qtdCabecalho > 0) {
    parcelas = Array.from({ length: qtdCabecalho }).map((_, index) => ({
      numero_parcela: index + 1,
      valor: null,
      percentual: null,
      tipo_documento: "",
      meio_pagamento: "",
    }));
  }

  return parcelas.sort((a, b) => a.numero_parcela - b.numero_parcela);
}

function extractOrderPaymentInfo(rawInput) {
  const raw = extractRawOrder(rawInput);
  const cabecalho = raw?.cabecalho || raw?.Cabecalho || {};
  const installments = extractOrderInstallments(rawInput);

  const codigoParcela = String(
    pickFirst(cabecalho?.codigo_parcela, cabecalho?.CodigoParcela, "")
  )
    .trim()
    .toUpperCase();

  const isBoletoByHeader = codigoParcela.includes("BOL");
  const isBoletoByInstallment = installments.some(
    (item) => item.tipo_documento === "BOL" || item.meio_pagamento === "15"
  );

  const count = installments.length || Number(toFiniteNumber(cabecalho?.qtde_parcelas) || 1);
  const isBoleto = isBoletoByHeader || isBoletoByInstallment;

  let paymentTypeLabel = "A vista";
  if (isBoleto) paymentTypeLabel = "Boleto";
  else if (count > 1) paymentTypeLabel = "Parcelado";

  return {
    isBoleto,
    installmentsCount: count > 0 ? count : 1,
    paymentTypeLabel,
    installmentsLabel: `${count > 0 ? count : 1}x`,
  };
}

function normalizeOrderNumberForMatch(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  const semParcela = text.replace(/\s*-\s*PARCELA\s+\d+\s*\/\s*\d+/i, "").trim();
  const digits = semParcela.replace(/\D/g, "");
  return digits || semParcela.toUpperCase();
}

function mergeItems(orderItems, savedItems, defaultPercent) {
  const defaultPercentStr =
    defaultPercent === null || defaultPercent === undefined ? "" : String(defaultPercent);

  if (orderItems.length === 0 && savedItems.length > 0) {
    return savedItems.map((item) => ({
      ...item,
      commission_percent:
        item.commission_percent === "" ? defaultPercentStr : String(item.commission_percent),
    }));
  }

  return orderItems.map((item, index) => {
    const byCode = savedItems.find((saved) => String(saved.item_code) === String(item.item_code));
    const byIndex = savedItems[index];
    const saved = byCode || byIndex || null;

    return {
      ...item,
      commission_percent:
        saved?.commission_percent !== undefined && saved?.commission_percent !== null
          ? String(saved.commission_percent)
          : defaultPercentStr,
    };
  });
}

async function buscarPedidoOmieRelacionado({ data, itemInicial, editId }) {
  const orderNumber = pickFirst(data?.order_number, itemInicial?.numero, itemInicial?.order_number);
  if (!orderNumber) return null;
  const orderNumberNormalized = normalizeOrderNumberForMatch(orderNumber);
  const orderNumberQuery = orderNumberNormalized || String(orderNumber);

  try {
    const { data: pedidosOmie } = await Api.get("/omie/orders", {
      params: { q: String(orderNumberQuery), limit: 50 },
    });

    return (
      (pedidosOmie || []).find(
        (o) =>
          String(o?.ledger_entry_id || "") === String(editId || "") ||
          normalizeOrderNumberForMatch(o?.order_number) === orderNumberNormalized
      ) || null
    );
  } catch (error) {
    console.warn("Nao foi possivel buscar pedido Omie relacionado.", error);
    return null;
  }
}

async function carregarProdutosDoLancamento({ data, itemInicial, editId }) {
  const fromLedger = parseSavedCommissionItems(
    pickFirst(
      data?.commission_items_json,
      data?.commission_items,
      data?.products_json,
      data?.products,
      data?.items_json
    )
  );
  if (fromLedger.length > 0) return fromLedger;

  const fromItemInicial = parseSavedCommissionItems(
    pickFirst(
      itemInicial?.commission_items_json,
      itemInicial?.commission_items,
      itemInicial?.products_json,
      itemInicial?.products,
      itemInicial?.items_json
    )
  );
  if (fromItemInicial.length > 0) return fromItemInicial;

  const rawLedgerItems = extractOrderItems(pickFirst(data?.raw_json, data?.source_raw_json));
  if (rawLedgerItems.length > 0) {
    return mergeItems(rawLedgerItems, [], data?.commission_percent);
  }

  const rawItemInicial = extractOrderItems(itemInicial?.raw_json);
  if (rawItemInicial.length > 0) {
    return mergeItems(
      rawItemInicial,
      [],
      pickFirst(data?.commission_percent, itemInicial?.percentual)
    );
  }

  const orderNumber = pickFirst(data?.order_number, itemInicial?.numero, itemInicial?.order_number);
  if (!orderNumber) return [];

  const pedido = await buscarPedidoOmieRelacionado({ data, itemInicial, editId });
  if (!pedido) return [];

  const savedItemsOmie = parseSavedCommissionItems(
    pickFirst(pedido?.commission_items_json, pedido?.commission_items)
  );
  const rawItemsOmie = extractOrderItems(pedido?.raw_json);

  return mergeItems(
    rawItemsOmie,
    savedItemsOmie,
    pickFirst(pedido?.commission_percent, data?.commission_percent)
  );
}

async function carregarResumoPedidoOmie({ data, itemInicial, editId }) {
  const pedido = await buscarPedidoOmieRelacionado({ data, itemInicial, editId });
  if (!pedido) return null;

  const rawValues = extractOrderValues(pedido?.raw_json);
  const paymentInfo = extractOrderPaymentInfo(pedido?.raw_json);
  const total = toFiniteNumber(
    pickFirst(rawValues.total_value, pedido?.total_value, data?.purchase_amount)
  );
  const productsGross = toFiniteNumber(rawValues.products_value);
  const products = toFiniteNumber(
    pickFirst(rawValues.products_net_value, pedido?.products_value, rawValues.products_value)
  );
  const freight = toFiniteNumber(
    pickFirst(rawValues.freight_value, pedido?.freight_value)
  );
  const discount = toFiniteNumber(rawValues.discount_value);
  const discountStored = toFiniteNumber(pedido?.discount_value);

  const valorCompra =
    products !== null
      ? products
      : (total !== null && freight !== null
          ? Number((total - freight).toFixed(2))
          : toNumber(data?.purchase_amount, 0));

  const valorFrete =
    freight !== null
      ? freight
      : (total !== null ? Number((total - valorCompra).toFixed(2)) : 0);

  const totalPedido =
    total !== null ? total : Number((valorCompra + valorFrete).toFixed(2));

  const valorDesconto =
    discountStored !== null
      ? discountStored
      : (discount !== null
          ? discount
      : (productsGross !== null
          ? Number(Math.max(0, productsGross - valorCompra).toFixed(2))
          : 0));

  return {
    valorCompra: Number(Math.max(0, valorCompra).toFixed(2)),
    valorDesconto: Number(Math.max(0, valorDesconto).toFixed(2)),
    valorFrete: Number(Math.max(0, valorFrete).toFixed(2)),
    totalPedido: Number(Math.max(0, totalPedido).toFixed(2)),
    paymentTypeLabel: paymentInfo.paymentTypeLabel,
    installmentsLabel: paymentInfo.installmentsLabel,
    installmentsCount: paymentInfo.installmentsCount,
    isBoleto: paymentInfo.isBoleto,
  };
}

function rebalanceItemTotals(items, targetTotalInput) {
  if (!Array.isArray(items) || items.length === 0) return items;

  const targetTotal = toFiniteNumber(targetTotalInput);
  if (targetTotal === null || targetTotal <= 0) return items;

  const currentTotal = Number(
    items.reduce((acc, item) => acc + toNumber(item.item_total, 0), 0).toFixed(2)
  );

  if (currentTotal <= 0) return items;
  if (Math.abs(currentTotal - targetTotal) < 0.01) return items;

  let acumulado = 0;
  return items.map((item, index) => {
    if (index === items.length - 1) {
      const ultimo = Number((targetTotal - acumulado).toFixed(2));
      return { ...item, item_total: ultimo >= 0 ? ultimo : 0 };
    }

    const proporcional = Number(
      ((toNumber(item.item_total, 0) * targetTotal) / currentTotal).toFixed(2)
    );
    acumulado += proporcional;
    return { ...item, item_total: proporcional };
  });
}

function buildManualItem(index) {
  return {
    row_key: `manual_${Date.now()}_${index}`,
    item_code: `MANUAL_${index + 1}`,
    description: "",
    quantity: 1,
    unit_price: 0,
    item_total: 0,
    commission_percent: "",
  };
}

const FormularioComissao = ({
  licenciados = [],
  lancamentoAdicionado,
  modo = "create",
  editId,
  itemInicial,
  lancamentoAtualizado,
}) => {
  const [aberto, setAberto] = useState(modo === "edit");

  const [user_id, setUserId] = useState("");
  const [entry_type, setentry_type] = useState("");
  const [status, setStatus] = useState("");
  const [purchase_date, setpurchase_date] = useState("");
  const [client_name, setclient_name] = useState("");
  const [order_number, setNumeroPedido] = useState("");
  const [purchase_amount, setpurchase_amount] = useState("");
  const [commission_percent, setcommission_percent] = useState("");
  const [description, setdescription] = useState("");
  const [dataInclusao, setDataInclusao] = useState("");
  const [commissionItems, setCommissionItems] = useState([]);
  const [pedidoOmieResumo, setPedidoOmieResumo] = useState(null);
  const [processandoSeparacao, setProcessandoSeparacao] = useState(false);

  const tipodelancamento = useMemo(
    () => [
      { label: "Credito", value: "CREDIT" },
      { label: "Debito", value: "DEBIT" },
    ],
    []
  );

  const listastatus = useMemo(
    () => [
      { label: "Pendente", value: "PENDING" },
      { label: "Confirmado", value: "CONFIRMED" },
      { label: "Saque solicitado", value: "WITHDRAW_REQUESTED" },
    ],
    []
  );

  const listaLicenciados = useMemo(() => {
    return (licenciados || []).map((l) => ({
      label: l.name,
      value: String(l.id),
    }));
  }, [licenciados]);

  const usarProdutos = modo === "edit" && entry_type !== "DEBIT";
  const temProdutosConfigurados = commissionItems.length > 0;
  const mostrarResumoOmie = modo === "edit" && entry_type !== "DEBIT" && Boolean(pedidoOmieResumo);
  const statusCanonico =
    String(status || "").trim().toUpperCase() === "CANCELLED"
      ? "CANCELED"
      : String(status || "").trim().toUpperCase();
  const descricaoTemParcela = /PARCELA\s+\d+\s*\/\s*\d+/i.test(String(description || ""));
  const pedidoParceladoBoleto =
    mostrarResumoOmie &&
    Boolean(pedidoOmieResumo?.isBoleto) &&
    Number(pedidoOmieResumo?.installmentsCount || 0) > 1;
  const podeSepararParcelas =
    modo === "edit" &&
    Boolean(editId) &&
    entry_type === "CREDIT" &&
    statusCanonico === "CONFIRMED" &&
    pedidoParceladoBoleto &&
    !descricaoTemParcela;

  const totalProdutos = useMemo(() => {
    if (!temProdutosConfigurados) return 0;
    return Number(
      commissionItems
        .reduce((acc, item) => acc + toNumber(item.item_total, 0), 0)
        .toFixed(2)
    );
  }, [commissionItems, temProdutosConfigurados]);

  const valorPrevistoProdutos = useMemo(() => {
    if (!temProdutosConfigurados) return 0;
    const geral = toFiniteNumber(commission_percent);

    return Number(
      commissionItems
        .reduce((acc, item) => {
          const pct = resolvePercent(item.commission_percent, geral);
          if (pct === null || pct < 0) return acc;
          return acc + calcCommissionValue(item.item_total, pct);
        }, 0)
        .toFixed(2)
    );
  }, [commissionItems, commission_percent, temProdutosConfigurados]);

  useEffect(() => {
    if (modo === "edit") setAberto(true);
  }, [modo, editId]);

  useEffect(() => {
    if (modo !== "edit" || !editId) return;

    let ativo = true;
    setPedidoOmieResumo(null);

    (async () => {
      try {
        const { data } = await Api.get(`/ledger-entries/${editId}`);

        setUserId(String(data.user_id || ""));
        setentry_type(data.entry_type || "");
        setStatus(data.status || "");

        setpurchase_date((data.purchase_date || "").slice(0, 10));
        setclient_name(data.client_name || "");
        setNumeroPedido(data.order_number || "");
        setpurchase_amount(String(Number(data.purchase_amount || 0)));
        setcommission_percent(
          data.commission_percent == null ? "" : String(data.commission_percent)
        );
        setdescription(data.description || "");
        setDataInclusao((data.entry_date || "").slice(0, 10));

        const [savedItems, resumoOmie] = await Promise.all([
          carregarProdutosDoLancamento({ data, itemInicial, editId }),
          carregarResumoPedidoOmie({ data, itemInicial, editId }),
        ]);

        if (!ativo) return;

        const ajustados = resumoOmie
          ? rebalanceItemTotals(savedItems, resumoOmie.valorCompra)
          : savedItems;

        setCommissionItems(ajustados);
        setPedidoOmieResumo(resumoOmie);
        if (resumoOmie && (data.entry_type || "") !== "DEBIT") {
          setpurchase_amount(String(resumoOmie.valorCompra));
        }
      } catch (_error) {
        alert("Erro ao carregar lancamento.");
      }
    })();

    return () => {
      ativo = false;
    };
  }, [modo, editId, itemInicial]);

  useEffect(() => {
    if (modo === "edit") return;
    setCommissionItems([]);
    setPedidoOmieResumo(null);
  }, [modo]);

  const aplicarPercentualGeral = (value) => {
    setcommission_percent(value);
    if (!commissionItems.length) return;

    setCommissionItems((prev) =>
      prev.map((item) => ({
        ...item,
        commission_percent: value,
      }))
    );
  };

  const adicionarProduto = () => {
    setCommissionItems((prev) => [...prev, buildManualItem(prev.length)]);
  };

  const removerProduto = (rowKey) => {
    setCommissionItems((prev) => prev.filter((item) => item.row_key !== rowKey));
  };

  const alterarProduto = (rowKey, field, value) => {
    setCommissionItems((prev) =>
      prev.map((item) =>
        item.row_key === rowKey
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const montarProdutosPayload = (generalPercent) => {
    if (!usarProdutos || !commissionItems.length) {
      return {
        payloadItems: [],
        purchaseTotal: null,
        weightedPercent: generalPercent,
      };
    }

    const payloadItems = [];
    let purchaseTotal = 0;
    let totalCommission = 0;

    for (let index = 0; index < commissionItems.length; index += 1) {
      const item = commissionItems[index];
      const descricao = String(item.description || "").trim();
      const itemTotal = toFiniteNumber(item.item_total);
      const rowLabel = `Produto ${index + 1}`;

      const isBlankRow =
        descricao === "" &&
        (itemTotal === null || itemTotal <= 0) &&
        String(item.commission_percent || "").trim() === "";

      if (isBlankRow) continue;

      if (itemTotal === null || itemTotal <= 0) {
        return {
          error: `Informe um valor valido para ${rowLabel}.`,
        };
      }

      const pct = resolvePercent(item.commission_percent, generalPercent);
      if (pct === null || !Number.isFinite(pct) || pct < 0) {
        return {
          error: `Informe um % de comissao valido para ${rowLabel}.`,
        };
      }

      const commissionValue = calcCommissionValue(itemTotal, pct);
      const quantity = Math.max(1, toNumber(item.quantity, 1));
      const unitPrice = Number((itemTotal / quantity).toFixed(2));
      purchaseTotal += itemTotal;
      totalCommission += commissionValue;

      payloadItems.push({
        item_code: item.item_code || `MANUAL_${index + 1}`,
        description: descricao || rowLabel,
        quantity,
        unit_price: unitPrice,
        item_total: itemTotal,
        commission_percent: pct,
        commission_value: commissionValue,
      });
    }

    if (payloadItems.length === 0) {
      return {
        error: "Adicione ao menos um produto valido ou remova a secao de produtos.",
      };
    }

    if (Number(totalCommission.toFixed(2)) <= 0) {
      return {
        error: "A comissao total dos produtos precisa ser maior que zero.",
      };
    }

    const weightedPercent =
      purchaseTotal > 0
        ? Number(((totalCommission / purchaseTotal) * 100).toFixed(6))
        : generalPercent;

    return {
      payloadItems,
      purchaseTotal: Number(purchaseTotal.toFixed(2)),
      weightedPercent,
    };
  };

  const aoSalvar = async (evento) => {
    evento.preventDefault();

    if (!user_id) return alert("Selecione o licenciado.");
    if (!entry_type) return alert("Selecione o tipo de lancamento.");
    if (!status) return alert("Selecione o status.");
    if (!purchase_date) return alert("Informe a data da compra.");
    if (!dataInclusao) return alert("Informe a data de inclusao.");

    const generalPercent = toFiniteNumber(commission_percent);
    if (commission_percent !== "" && (generalPercent === null || generalPercent < 0)) {
      return alert("Informe um % de comissao valido.");
    }

    let purchaseAmountNumber = toFiniteNumber(purchase_amount);
    if (purchaseAmountNumber === null || purchaseAmountNumber <= 0) {
      return alert("Informe o valor da compra/saque.");
    }

    let commissionPercentNumber =
      commission_percent === "" || commission_percent === null || commission_percent === undefined
        ? null
        : generalPercent;

    let commissionItemsPayload = [];
    if (usarProdutos && commissionItems.length > 0) {
      const produtosPayload = montarProdutosPayload(generalPercent);
      if (produtosPayload.error) return alert(produtosPayload.error);

      purchaseAmountNumber = produtosPayload.purchaseTotal;
      commissionPercentNumber = produtosPayload.weightedPercent;
      commissionItemsPayload = produtosPayload.payloadItems;
    }

    if (entry_type !== "DEBIT" && pedidoOmieResumo) {
      const valorCompraResumo = Number(pedidoOmieResumo.valorCompra || 0);
      const valorFreteResumo = Number(pedidoOmieResumo.valorFrete || 0);
      const totalPedidoResumo = Number(pedidoOmieResumo.totalPedido || 0);
      const totalEsperado = Number((valorCompraResumo + valorFreteResumo).toFixed(2));

      if (valorCompraResumo <= 0) {
        return alert("Valor da compra invalido no pedido Omie.");
      }

      if (totalPedidoResumo <= 0 || totalPedidoResumo + 0.01 < valorCompraResumo) {
        return alert("Total do pedido invalido: precisa ser maior ou igual ao valor da compra.");
      }

      if (Math.abs(totalEsperado - totalPedidoResumo) > 0.05) {
        return alert(
          "Valores do pedido inconsistentes: valor da compra + frete nao confere com o total do pedido."
        );
      }

      purchaseAmountNumber = valorCompraResumo;
    }

    const payload = {
      user_id: Number(user_id),
      entry_type,
      purchase_amount: purchaseAmountNumber,
      commission_percent: commissionPercentNumber,
      purchase_date,
      entry_date: dataInclusao,
      client_name: client_name || null,
      order_number: order_number || null,
      description: description || null,
      origin: entry_type === "DEBIT" ? "WITHDRAW" : "COMMISSION",
      status,
    };

    if (entry_type === "DEBIT") {
      payload.commission_items = [];
    } else if (commissionItemsPayload.length > 0) {
      payload.commission_items = commissionItemsPayload;
    }

    try {
      if (modo === "edit" && editId) {
        const { data } = await Api.put(`/ledger-entries/${editId}`, payload);
        lancamentoAtualizado?.(data);
      } else {
        const { data } = await Api.post("/ledger-entries", payload);
        lancamentoAdicionado?.(data);
      }
    } catch (err) {
      console.error("Erro ao salvar:", err?.response?.data || err.message);
      alert(err?.response?.data?.message || "Erro ao salvar lancamento.");
      return;
    }

    setUserId("");
    setentry_type("");
    setStatus("");
    setpurchase_date("");
    setclient_name("");
    setNumeroPedido("");
    setpurchase_amount("");
    setcommission_percent("");
    setdescription("");
    setDataInclusao("");
    setCommissionItems([]);
  };

  const separarParcelas = async () => {
    if (!podeSepararParcelas || !editId) return;

    const confirmar = window.confirm(
      `Separar este lancamento em ${pedidoOmieResumo?.installmentsLabel || "parcelas"}?`
    );
    if (!confirmar) return;

    try {
      setProcessandoSeparacao(true);

      const { data } = await Api.post(`/ledger-entries/${editId}/split-installments`);
      const parcelasGeradas = Number(data?.installments_count || data?.ledger_entries?.length || 0);

      alert(
        parcelasGeradas > 0
          ? `Parcelas geradas com sucesso (${parcelasGeradas}).`
          : "Parcelas geradas com sucesso."
      );

      const primeiroNovoLancamento = Array.isArray(data?.ledger_entries)
        ? data.ledger_entries[0]
        : null;

      lancamentoAtualizado?.(
        primeiroNovoLancamento || {
          id: editId,
          status: "CANCELED",
        }
      );
    } catch (err) {
      alert(err?.response?.data?.message || "Nao foi possivel separar as parcelas.");
    } finally {
      setProcessandoSeparacao(false);
    }
  };

  return (
    <section className="card-comissao">
      <div className="card-header-comissao">
        <div>
          <div className="card-title">
            {modo === "edit" ? "Editar lancamento" : "Novo lancamento"}
          </div>
          <div className="card-subtitle">
            Registre manualmente a comissao ou o saque no extrato do licenciado.
          </div>
        </div>

        <button
          type="button"
          onClick={() => setAberto((prev) => !prev)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 22,
            display: "flex",
            alignItems: "center",
            color: "#555",
          }}
          aria-label="Abrir ou fechar formulario"
        >
          {aberto ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>

      {aberto && (
        <form id="bank-form" className="lancamentoForm" noValidate onSubmit={aoSalvar}>
          <FormularioList
            itens={listaLicenciados}
            title="Licenciado"
            required="required"
            id="user_id"
            placeholder="Selecione"
            helper="Selecione o licenciado"
            lista="user_id"
            valor={user_id}
            aoAlterado={(v) => setUserId(v)}
          />

          <FormularioList
            itens={tipodelancamento}
            title="Tipo de lancamento"
            required="required"
            id="entry_type"
            placeholder="Selecione"
            helper="Selecione o tipo de lancamento"
            lista="entry_type"
            valor={entry_type}
            aoAlterado={(v) => setentry_type(v)}
          />

          <FormularioList
            itens={listastatus}
            title="Status"
            required="required"
            id="status"
            placeholder="Selecione"
            helper="Selecione o status"
            lista="status"
            valor={status}
            aoAlterado={(v) => setStatus(v)}
          />

          <FormularioInput
            type="date"
            size="quarto"
            title="Data da compra"
            required="required"
            id="dataCompra"
            placeholder="dd/mm/aaaa"
            helper="Informe a data de compra."
            valor={purchase_date}
            aoAlterado={(v) => setpurchase_date(v)}
          />

          <FormularioInput
            type="text"
            size="quarto"
            title="Cliente (nome ou referencia)"
            required="required"
            id="cliente"
            placeholder="Cliente XPTO / Saque Licenciado"
            helper="Informe o nome do cliente."
            valor={client_name}
            aoAlterado={(v) => setclient_name(v)}
          />

          <FormularioInput
            type="text"
            size="quarto"
            title="N da compra / saque"
            required="required"
            id="numeroPedido"
            placeholder="Ex: 12345 / SAQ-2026-001"
            helper="Informe o numero da compra."
            valor={order_number}
            aoAlterado={(v) => setNumeroPedido(v)}
          />

          <FormularioInput
            type="number"
            size="quarto"
            title="Valor da compra, ou saque (R$)"
            required="required"
            id="purchase_amount"
            placeholder="00,00"
            helper={
              mostrarResumoOmie
                ? `Base da comissao sem frete: ${formatCurrencyBRL(
                    pedidoOmieResumo?.valorCompra
                  )}`
                : usarProdutos && temProdutosConfigurados
                ? `Total dos produtos: ${formatCurrencyBRL(totalProdutos)}`
                : "Valor da compra."
            }
            valor={
              mostrarResumoOmie
                ? String(pedidoOmieResumo?.valorCompra || 0)
                : usarProdutos && temProdutosConfigurados
                ? String(totalProdutos)
                : purchase_amount
            }
            readOnly={mostrarResumoOmie || (usarProdutos && temProdutosConfigurados)}
            aoAlterado={(v) => setpurchase_amount(v)}
          />

          {mostrarResumoOmie && (
            <>
              <FormularioInput
                type="text"
                size="quarto"
                title="Forma de pagamento"
                id="forma_pagamento_omie_edit"
                helper="Somente conferencia."
                valor={`${pedidoOmieResumo?.paymentTypeLabel || "A vista"}${
                  pedidoOmieResumo?.installmentsCount > 1
                    ? ` (${pedidoOmieResumo?.installmentsLabel})`
                    : ""
                }`}
                readOnly
                aoAlterado={() => {}}
              />

              <FormularioInput
                type="number"
                size="quarto"
                title="Frete (R$)"
                id="frete_omie_edit"
                helper="Somente conferencia."
                valor={String(pedidoOmieResumo?.valorFrete || 0)}
                readOnly
                aoAlterado={() => {}}
              />

              <FormularioInput
                type="number"
                size="quarto"
                title="Desconto (R$)"
                id="desconto_omie_edit"
                helper="Somente conferencia."
                valor={String(pedidoOmieResumo?.valorDesconto || 0)}
                readOnly
                aoAlterado={() => {}}
              />

              <FormularioInput
                type="number"
                size="quarto"
                title="Total do pedido (R$)"
                id="total_pedido_omie_edit"
                helper="Somente conferencia."
                valor={String(pedidoOmieResumo?.totalPedido || 0)}
                readOnly
                aoAlterado={() => {}}
              />

              {pedidoParceladoBoleto && (
                <div className="ledger-split-card">
                  <div className="ledger-split-content">
                    <div className="ledger-split-title">Separar parcelas</div>
                    <div className="ledger-split-subtitle">
                      Gere os lancamentos por parcela para pedidos de boleto ja confirmados.
                    </div>
                    <div className="ledger-split-meta">
                      Parcelamento identificado: {pedidoOmieResumo?.installmentsLabel}
                    </div>
                    {!descricaoTemParcela && statusCanonico !== "CONFIRMED" && (
                      <div className="ledger-split-warning">
                        Para separar parcelas, o status precisa estar como Confirmado.
                      </div>
                    )}
                    {descricaoTemParcela && (
                      <div className="ledger-split-warning">
                        Este lancamento ja esta separado em parcelas.
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="ledger-split-action"
                    onClick={separarParcelas}
                    disabled={!podeSepararParcelas || processandoSeparacao}
                  >
                    {processandoSeparacao ? "Separando..." : "Separar parcelas"}
                  </button>
                </div>
              )}
            </>
          )}

          <FormularioInput
            type="number"
            size="quarto"
            title="Percentual de comissao (%)"
            id="commission_percent"
            placeholder="Ex: 18"
            helper={
              usarProdutos
                ? `Comissao prevista pelos produtos: ${formatCurrencyBRL(
                    valorPrevistoProdutos
                  )}. Se preencher, aplica em todos os produtos.`
                : "% de comissao."
            }
            valor={commission_percent}
            aoAlterado={aplicarPercentualGeral}
          />

          <FormularioInput
            type="text"
            size="quarto"
            title="Descricao"
            id="description"
            placeholder="Ex: Pedido #1234 - site, Saque solicitado 10/01/2026"
            helper="Informe uma descricao."
            valor={description}
            aoAlterado={(v) => setdescription(v)}
          />

          <FormularioInput
            type="date"
            size="quarto"
            title="Data da inclusao do lancamento"
            required="required"
            id="dataInclusao"
            placeholder="dd/mm/aaaa"
            helper="Informe a data de inclusao."
            valor={dataInclusao}
            aoAlterado={(v) => setDataInclusao(v)}
          />

          {usarProdutos && (
            <div className="ledger-products-section">
              <div className="ledger-products-header">
                <div className="ledger-products-title">Produtos comprados</div>
                <div className="ledger-products-subtitle">
                  Ajuste o percentual de comissao individual de cada produto.
                </div>
              </div>

              <div className="ledger-products-toolbar">
                <button
                  type="button"
                  className="ledger-products-add"
                  onClick={adicionarProduto}
                >
                  <FiPlus />
                  <span>Adicionar produto</span>
                </button>
                {temProdutosConfigurados && (
                  <div className="ledger-products-total">
                    Total produtos: {formatCurrencyBRL(totalProdutos)}
                  </div>
                )}
              </div>

              {commissionItems.length === 0 ? (
                <div className="ledger-products-empty">
                  Nenhum produto cadastrado. Clique em "Adicionar produto".
                </div>
              ) : (
                <div className="ledger-products-list">
                  {commissionItems.map((item, index) => {
                    const pct = resolvePercent(
                      item.commission_percent,
                      toFiniteNumber(commission_percent)
                    );
                    const itemCommission =
                      pct === null || pct < 0 ? 0 : calcCommissionValue(item.item_total, pct);

                    return (
                      <div className="ledger-product-row" key={item.row_key}>
                        <div className="ledger-product-grid">
                          <div className="ledger-inline-field">
                            <label htmlFor={`produto_nome_${item.row_key}`}>
                              Produto {index + 1}
                            </label>
                            <input
                              id={`produto_nome_${item.row_key}`}
                              type="text"
                              placeholder="Nome do produto"
                              value={item.description}
                              onChange={(event) =>
                                alterarProduto(item.row_key, "description", event.target.value)
                              }
                            />
                          </div>

                          <div className="ledger-inline-field">
                            <label htmlFor={`produto_total_${item.row_key}`}>Valor (R$)</label>
                            <input
                              id={`produto_total_${item.row_key}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={item.item_total}
                              onChange={(event) =>
                                alterarProduto(item.row_key, "item_total", event.target.value)
                              }
                            />
                          </div>

                          <div className="ledger-inline-field">
                            <label htmlFor={`produto_pct_${item.row_key}`}>Comissao (%)</label>
                            <input
                              id={`produto_pct_${item.row_key}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="%"
                              value={item.commission_percent}
                              onChange={(event) =>
                                alterarProduto(
                                  item.row_key,
                                  "commission_percent",
                                  event.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="ledger-product-footer">
                          <div className="ledger-product-preview">
                            Comissao prevista: {formatCurrencyBRL(itemCommission)}
                          </div>
                          <button
                            type="button"
                            className="ledger-product-remove"
                            onClick={() => removerProduto(item.row_key)}
                            title="Remover produto"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <FormularioButom icone="💾" disabled={processandoSeparacao}>
              {modo === "edit" ? "Salvar alteracoes" : "Salvar lancamento"}
            </FormularioButom>
          </div>
        </form>
      )}
    </section>
  );
};

export default FormularioComissao;
